// Reiter "Dubletten": Filter nach Art, die Gruppen als Karten, "Mehr laden". Lädt neu, wenn sich Filter ändern, nach jeder Handlung und
// wenn der Server `lead.merged` oder `lead.deleted` meldet.
import { useCallback, useEffect, useRef, useState } from "react";
import { dublettenApi, type DublettenArt, type DublettenGruppe, type ZusammenfuehrenErgebnis } from "../api/dubletten";
import { useEchtzeit } from "../api/echtzeit";
import { Laden, Leer } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { useMeldung } from "../components/Meldungen";
import { GruppeKarte } from "./GruppeKarte";
import { ART_FILTER, TX } from "./texte";

const SEITE = 20;

export function GruppenListe({ art, ignorierte, onArt, onIgnorierte }: {
  art: DublettenArt;
  ignorierte: boolean;
  onArt: (a: DublettenArt) => void;
  onIgnorierte: (an: boolean) => void;
}) {
  const meldung = useMeldung();
  const [gruppen, setGruppen] = useState<DublettenGruppe[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [gesamt, setGesamt] = useState(0);
  const [fehler, setFehler] = useState<unknown>(null);
  const [mehrLaedt, setMehrLaedt] = useState(false);
  const zaehler = useRef(0);

  const laden = useCallback(
    async (still = false) => {
      const nr = ++zaehler.current;
      if (!still) setGruppen(null);
      try {
        const r = await dublettenApi.gruppen({ art, ignorierte, limit: SEITE });
        if (nr !== zaehler.current) return;
        setGruppen(r.data);
        setCursor(r.has_more ? r.cursor : null);
        setGesamt(r.total);
        setFehler(null);
      } catch (e) {
        if (nr === zaehler.current) setFehler(e);
      }
    },
    [art, ignorierte],
  );
  useEffect(() => {
    void laden();
  }, [laden]);
  useEchtzeit(["lead.merged", "lead.deleted"], () => void laden(true));

  const mehr = async () => {
    if (!cursor) return;
    setMehrLaedt(true);
    try {
      const r = await dublettenApi.gruppen({ art, ignorierte, limit: SEITE, cursor });
      setGruppen((g) => [...(g ?? []), ...r.data]);
      setCursor(r.has_more ? r.cursor : null);
      setGesamt(r.total);
    } catch (e) {
      meldung.fehler(e, "Nicht geladen");
    } finally {
      setMehrLaedt(false);
    }
  };

  const fertig = (e: ZusammenfuehrenErgebnis) => {
    meldung.zeige(
      "ok",
      `${TX.zfFertig} ${TX.zfFertigClose}`,
      e.protokoll_id
        ? {
            label: TX.rueckgaengig,
            onClick: () =>
              void dublettenApi
                .rueckgaengig(e.protokoll_id as number)
                .then(() => {
                  meldung.ok("Zusammenführung rückgängig gemacht.");
                  void laden(true);
                })
                .catch((err) => meldung.fehler(err, "Nicht rückgängig gemacht")),
          }
        : undefined,
      12000,
    );
    void laden(true);
  };

  return (
    <>
      <div className="dub-leiste">
        <div className="dub-leiste__filter" role="group" aria-label="Art der Übereinstimmung">
          {ART_FILTER.map((f) => (
            <button key={f.id} type="button" className={`filterchip${art === f.id ? " filterchip--an" : ""}`} aria-pressed={art === f.id} onClick={() => onArt(f.id)}>
              {f.titel}
            </button>
          ))}
        </div>
        <div className="dub-leiste__rechts">
          <label className="dub-haken">
            <input type="checkbox" checked={ignorierte} onChange={(e) => onIgnorierte(e.target.checked)} />
            {TX.ignorierteZeigen}
          </label>
          {gruppen ? <span className="dub-zahl">{gesamt} {gesamt === 1 ? "Gruppe" : "Gruppen"}</span> : null}
        </div>
      </div>
      {fehler ? <FehlerBox fehler={fehler} onNochmal={() => void laden()} /> : null}
      {!gruppen && !fehler ? <Laden /> : null}
      {gruppen && !gruppen.length ? <Leer titel={TX.keineGruppen} text={TX.keineGruppenText} /> : null}
      {gruppen?.length ? (
        <div className="dub-gruppen">
          {gruppen.map((g) => (
            <GruppeKarte key={`${g.art}|${g.wert}`} gruppe={g} onGeaendert={() => void laden(true)} onZusammengefuehrt={fertig} />
          ))}
        </div>
      ) : null}
      {cursor ? (
        <div className="dub-mehr">
          <button type="button" className="knopf" disabled={mehrLaedt} onClick={() => void mehr()}>
            {mehrLaedt ? "Wird geladen …" : TX.ladeMehr}
          </button>
        </div>
      ) : null}
    </>
  );
}
